import L from "leaflet";
import "../../Leaflet.Editable.js"
import * as C from "./RainRoverConst";
import * as M from "../../utils/OwnMapMath";
import * as ClipperLib from "clipper-lib";
import {kmlExport} from "./KmlExport";
import {exportWorkZones} from "./JsonExport";
import {v7 as uuidv7} from 'uuid';

class RainMap {

    static TAG ="RainMap";
    static SCALE = 1000.0;

    uuid = undefined;

    map = undefined;
    originLatLng = undefined;
    averageLocalZ = 0;

    geoFence = undefined;
    geoFencePolygon = undefined;

    workArea = undefined;
    workAreaPolygon = undefined;

    referencePath = undefined;
    referencePathPolyLine = undefined;

    waterSourcePos = undefined;
    waterSourceMarker = undefined;

    corridorWidth = undefined;
    curveRadius = undefined;


    drivingPaths = [];
    controlPoints = [];
    servicePathLines = [];

    nodeMarkers = [];
    routeMarkers = [];
    routeLengthMarkers = [];

    graph = undefined;
    graphLatLng = undefined;
    wateringSequence = undefined;
    kml = undefined;

    wateringRoutesPickedIndices = [];
    wateringMainPathsIndices = [];
    wateringSidePathsIndices = [];

    wateringMainPathsLines = [];
    wateringSidePathsLines = [];

    wateringRouteLines = [];
    wateringRoutePoints = [];
    wateringPolygons = [];

    mainPathsWorkZones = [];
    sidePathsWorkZones = [];

    cachedConfig = [];


    constructor(map, originLatLng) {
        this.uuid = uuidv7();
        this.map = map;
        this.originLatLng = originLatLng;
    }


    setGeoFence(geoFenceLatLng) {
        if (!this.originLatLng) {
            this.originLatLng = M.getAverageLatLng(geoFenceLatLng);
        }
        this.geoFencePolygon?.removeFrom(this.map);
        if (geoFenceLatLng && geoFenceLatLng.length >= 3) {
            this.geoFence = M.convertToWorkSpace(M.getLocalCoordinates(this.originLatLng, geoFenceLatLng), RainMap.SCALE);
            this.geoFencePolygon = L.polygon(geoFenceLatLng, C.geoFenceAreaOptions);
            this.geoFencePolygon.addTo(this.map);
        } else {
            this.geoFence = undefined;
            this.geoFencePolygon = undefined;
        }
    }


    setWorkArea(workAreaLatLng) {
        if (!this.originLatLng) {
            this.originLatLng = M.getAverageLatLng(workAreaLatLng);
        }
        this.workAreaPolygon?.removeFrom(this.map);
        if (workAreaLatLng && workAreaLatLng.length >= 3) {
            this.workArea = M.convertToWorkSpace(M.getLocalCoordinates(this.originLatLng, workAreaLatLng), RainMap.SCALE);
            this.workAreaPolygon = L.polygon(workAreaLatLng, C.workAreaOptions);
            this.workAreaPolygon.addTo(this.map);
        } else {
            this.workArea = undefined;
            this.workAreaPolygon = undefined;
        }
    }

    getWorkAreaLatLng() {
        if (this.workArea) {
            return M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(this.workArea, this.averageLocalZ, RainMap.SCALE));
        } else {
            return undefined;
        }
    }


    setReferencePath(lineLatLng) {
        if (!this.originLatLng) {
            this.originLatLng = M.getAverageLatLng(lineLatLng);
        }
        this.referencePathPolyLine?.removeFrom(this.map);
        if (lineLatLng && lineLatLng.length >= 2) {
            let localCoordinates = M.getLocalCoordinates(this.originLatLng, lineLatLng);
            this.averageLocalZ = M.getAverageZ(localCoordinates);
            this.referencePath = M.convertToWorkSpace(M.sortPoints(localCoordinates), RainMap.SCALE);
            this.referencePathPolyLine = L.polyline(lineLatLng, C.guideLinePathOptions);
            this.referencePathPolyLine.addTo(this.map);
        } else {
            this.referencePath = undefined;
            this.referencePathPolyLine = undefined;
        }
    }

    getReferencePathLatLng() {
        if (this.referencePath) {
            return M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(this.referencePath, this.averageLocalZ, RainMap.SCALE));
        } else {
            return undefined;
        }
    }

    getPathLatLng(path) {
        return M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(path, this.averageLocalZ, RainMap.SCALE));
    }

    getPointLatLng(point) {
        return M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace([point], this.averageLocalZ, RainMap.SCALE))[0];
    }



    setWaterSourcePos(posLatLng) {
        if (!this.originLatLng) {
            this.originLatLng = posLatLng;
        }
        if (posLatLng) {
            this.waterSourcePos = M.convertToWorkSpace(M.getLocalCoordinates(this.originLatLng, [posLatLng]), RainMap.SCALE)[0];
            if (this.waterSourceMarker) {
                this.waterSourceMarker.setLatLng(posLatLng);
            } else {
                this.rebuildWaterSourceMarker();
            }
        } else {
            this.waterSourceMarker?.removeFrom(this.map);
            this.waterSourceMarker = undefined;
            this.waterSourcePos = undefined;
        }
        // console.log(RainMap.TAG, "set water source pos", posLatLng, this.waterSourcePos, this.originLatLng);
    }


    getWaterSourcePosLatLng() {
        if (this.waterSourcePos) {
            return M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace([this.waterSourcePos], this.averageLocalZ, RainMap.SCALE))[0];
        } else {
            return undefined;
        }
    }


    rebuildWaterSourceMarker() {
        this.waterSourceMarker?.removeFrom(this.map);
        this.waterSourceMarker = L.marker(this.getWaterSourcePosLatLng(), C.waterSourceMarkerOptions);
        this.waterSourceMarker.addTo(this.map);
        this.waterSourceMarker.enableEdit();
    }


    hasGeoFence() {
        return !!this.geoFence;
    }

    hasWorkArea() {
        return !!this.workArea;
    };

    hasReferencePath() {
        return !!this.referencePath;
    }

    hasWaterSourcePos() {
        return !!this.waterSourcePos;
    }



    generateOffsetPaths(referencePathLine, maxBoundarySize, firstOffsetMeters, offsetMeters) {

        this.corridorWidth = offsetMeters;

        let offsetPathArray = [];

        let clipperOffset = new ClipperLib.ClipperOffset();
        clipperOffset.AddPath(referencePathLine, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenButt);

        let i = 1;
        const offsetBaseMM = firstOffsetMeters * RainMap.SCALE;
        const offsetMM = offsetMeters * RainMap.SCALE;
        let offsetAmount = offsetBaseMM;
        while (offsetAmount < maxBoundarySize * 2) {
            let polyTree = new ClipperLib.PolyTree();
            clipperOffset.Execute(polyTree, offsetAmount);
            offsetAmount = offsetBaseMM + i * offsetMM;
            ++i;

            let offsetPath = ClipperLib.Clipper.ClosedPathsFromPolyTree(polyTree);
            let closedPath = offsetPath[0];
            closedPath.push(closedPath[0]);
            // console.log(RainMap.TAG, "offset path", offsetPath);
            offsetPathArray.push(closedPath);
        }
        return offsetPathArray;
    }


    clipPaths(clippingPolyline, pathsArray) {
        let clipper = new ClipperLib.Clipper();
        clipper.AddPaths(pathsArray, ClipperLib.PolyType.ptSubject, false);
        clipper.AddPath(clippingPolyline, ClipperLib.PolyType.ptClip, true);
        let clippedPaths = new ClipperLib.PolyTree();
        clipper.Execute(ClipperLib.ClipType.ctIntersection, clippedPaths, ClipperLib.PolyFillType.pftPositive, ClipperLib.PolyFillType.pftNonZero);
        let result = ClipperLib.Clipper.OpenPathsFromPolyTree(clippedPaths);
        return M.mergePathsXY(result);
    }



    calculateDrivingPathsFromCache() {
        this.calculateDrivingPaths(...this.cachedConfig);
    }


    calculateDrivingPaths(firstOffsetMeters, offsetMeters, referencePathMaxDeviation, splitAngleDeg, minDistToServicePath, servicePathPull)  {

        firstOffsetMeters = Math.max(1, firstOffsetMeters);
        offsetMeters = Math.max(10, offsetMeters);
        referencePathMaxDeviation = Math.max(0.1, referencePathMaxDeviation);
        splitAngleDeg = Math.max(0, splitAngleDeg);
        minDistToServicePath = Math.max(0, minDistToServicePath);
        servicePathPull = Math.max(0, Math.min(1, servicePathPull));

        this.cachedConfig = [firstOffsetMeters, offsetMeters, referencePathMaxDeviation, splitAngleDeg, minDistToServicePath, servicePathPull];

        this.clearLinesAndMarkers();

        console.log(RainMap.TAG, "work area", this.workArea);

        let maxBoundarySize = M.getMaxDistanceXY(this.geoFence);
        console.log(RainMap.TAG, "max boundary size", maxBoundarySize);

        console.log(RainMap.TAG, "reference path", this.referencePath);

        let simplifiedPath = M.simplifyCurveXY(this.referencePath, referencePathMaxDeviation * RainMap.SCALE);
        console.log(RainMap.TAG, "simplified reference path", simplifiedPath);

        let orderedPath = M.orderPointsXY(simplifiedPath);
        console.log(RainMap.TAG, "ordered path", orderedPath);

        let extendedReferencePath = M.extendPathXY(orderedPath, maxBoundarySize);
        console.log(RainMap.TAG, "extended path", extendedReferencePath);

        const offsetPathArray = this.generateOffsetPaths(extendedReferencePath, maxBoundarySize, firstOffsetMeters, offsetMeters);
        console.log(RainMap.TAG, "offset paths", offsetPathArray);

        const drivingPaths = this.clipPaths(this.workArea, offsetPathArray);

        const scaledOffset = offsetMeters * RainMap.SCALE;

        let wateringExtensions = M.generateExtensionsXY(this.geoFence, drivingPaths, 0.5 * offsetMeters, offsetMeters, RainMap.SCALE).flat();
        // console.log(RainMap.TAG, "watering extensions", wateringExtensions);
        if (!M.isPointInPolygonXY(this.workArea, this.waterSourcePos)) {
            wateringExtensions = wateringExtensions.filter(ext => ext.every(p => M.distXY(p, this.waterSourcePos) > 2 * scaledOffset));
        }
        const wateringExtensionsWithType = wateringExtensions.map(points => ({type: C.pathTypeWateringExtension, points: points}));

        console.log(RainMap.TAG, "watering extensions", wateringExtensionsWithType);

        const graphPaths = [...wateringExtensionsWithType];

        let drivingNetwork = M.generateDrivingNetworkXY(this.waterSourcePos, drivingPaths, this.geoFence, this.workArea,
            offsetMeters, 10, splitAngleDeg * Math.PI / 180.0,
            minDistToServicePath, servicePathPull, RainMap.SCALE
        );

        console.log(RainMap.TAG, "driving network", drivingNetwork);
        let servicePathLine = drivingNetwork.servicePath;
        if (servicePathLine && servicePathLine.length > 1) {
            console.log(RainMap.TAG, "service path line", servicePathLine);
            for (let i = 0; i < servicePathLine.length - 1; ++i) {
                graphPaths.push({type: C.pathTypeService, points: [servicePathLine[i], servicePathLine[i+1]]});
            }
            drivingNetwork.wateringPaths.forEach(path => {
                graphPaths.push({type: C.pathTypeWatering, points: path});
            });
        } else {
            const clippedPathsWithType = drivingPaths.map(points => ({type: C.pathTypeWatering, points: points}));
            console.log(RainMap.TAG, "clipped path array", clippedPathsWithType);
            graphPaths.push(...clippedPathsWithType);
        }

        drivingNetwork.extensionPaths.forEach(extension => {
            graphPaths.push({type: C.pathTypeServiceExtension, points: extension});
        });

        drivingNetwork.headlandPaths.forEach(paths => {
            if (paths.length === 1) {
                graphPaths.push({type: C.pathTypeHeadland, points: paths[0]});
            } else {
                paths.forEach(path => {
                    graphPaths.push({type: C.pathTypeHeadland, points: path});
                });
                let extensions = M.generateExtensionsXY(this.geoFence, paths, 0.5 * offsetMeters, offsetMeters - 0.5, RainMap.SCALE);
                console.log(RainMap.TAG, "headland extensions", extensions);
                if (M.isPointInPolygonXY(this.workArea, this.waterSourcePos)) {
                    graphPaths.push({type: C.pathTypeHeadlandExtension, points: extensions[0][1]});
                    for (let i = 1; i < extensions.length - 1; ++i) {
                        for (let k = 0; k <= 1; ++k) {
                            graphPaths.push({type: C.pathTypeHeadlandExtension, points: extensions[i][k]});
                        }
                    }
                    graphPaths.push({type: C.pathTypeHeadlandExtension, points: extensions.at(-1)[0]});
                } else {
                    if (extensions[0][1].every(p => M.distXY(p, this.waterSourcePos) > 2 * scaledOffset)) {
                        graphPaths.push({type: C.pathTypeHeadlandExtension, points: extensions[0][1]});
                    }
                    for (let i = 1; i < extensions.length - 1; ++i) {
                        for (let k = 0; k <= 1; ++k) {
                            if (extensions[i][k].every(p => M.distXY(p, this.waterSourcePos) > 2 * scaledOffset)) {
                                graphPaths.push({type: C.pathTypeHeadlandExtension, points: extensions[i][k]});
                            }
                        }
                    }
                    if (extensions.at(-1)[1].every(p => M.distXY(p, this.waterSourcePos) > 2 * scaledOffset)) {
                        graphPaths.push({type: C.pathTypeHeadlandExtension, points: extensions.at(-1)[0]});
                    }
                }
            }
        });

        console.log(RainMap.TAG, "approach point", drivingNetwork.approachPoint);
        let approachPath;
        if (drivingNetwork.approachPoint) {
            approachPath = [this.waterSourcePos, drivingNetwork.approachPoint];
        } else {
            const closestPoint = M.findClosestEndpointXY(graphPaths.map(path => path.points), this.waterSourcePos);
            approachPath = [this.waterSourcePos, closestPoint];
        }
        graphPaths.push({type: C.pathTypeApproach, points: approachPath});

        this.graph = M.generateGraphXY(graphPaths, 0.1 * RainMap.SCALE);
        M.cleanupGraph(this.graph);
        console.log(RainMap.TAG, "graph", this.graph);

        this.graphLatLng = this.convertGraphToLatLng(this.graph);
        console.log(RainMap.TAG, "graph lat lng", this.graphLatLng);
        this.drawGraphLatLng(this.graphLatLng);
    }


    convertGraphToLatLng(graph) {
        return {
            edges: graph.edges,
            edgeTypes: graph.edgeTypes,
            nodes: graph.nodes,
            paths: graph.paths,
            pathLengths: graph.pathLengths,
            nodesLatLng: M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(graph.nodes, this.averageLocalZ, RainMap.SCALE)),
            pathsLatLng: graph.paths.map(path => M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(path, this.averageLocalZ, RainMap.SCALE))),
        }
    }


    drawGraphLatLng(graphLatLng) {
        graphLatLng.pathsLatLng.forEach((path, index) => {
            let line = L.polyline(path, {...C.generatedLinePathOptions, color: C.pathTypeColors[graphLatLng.edgeTypes[index]]});
            line.addTo(this.map);
            this.drivingPaths.push({polyLine: line});
        });
        graphLatLng.nodesLatLng.forEach((node, index) => {
            let marker = L.circleMarker(node, {...C.wateringPointOptions, id: index});
            marker.addTo(this.map);
            this.nodeMarkers.push(marker);
        });
    }


    download(filename, text) {
        let element = document.createElement('a');
        element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(text));
        element.setAttribute('download', filename);
        element.style.display = 'none';
        element.click();
    }


    exportKml() {
        if (this.graph) {
            this.graphLatLng = this.convertGraphToLatLng(this.graph);
            const headlandExterior =  M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(this.geoFence, this.averageLocalZ, RainMap.SCALE));
            const headlandInterior =  M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(this.workArea, this.averageLocalZ, RainMap.SCALE));
            this.kml = kmlExport(this.uuid, this.graphLatLng, headlandExterior, headlandInterior, this.wateringSequence, this.wateringMainPathsIndices, this.wateringSidePathsIndices);
            this.download("export.kml", this.kml);
        }
    }

    exportWorkZones() {
        const json = exportWorkZones(this.uuid, this.mainPathsWorkZones, this.sidePathsWorkZones);
        console.log(RainMap.TAG, "json", json);
        this.download("work_zones.json", json);
    }


    onMarkerDrag(evt) {
        // console.log(RainMap.TAG, evt);
        const index = evt.layer.options.id;

        if (index === C.waterSourceMarkerOptions.id) {
            // console.log(RainMap.TAG, "water", evt.latlng);
            this.setWaterSourcePos(evt.latlng);
            // console.log(RainMap.TAG, "water", this.waterSourcePos);
            // return;
        }

        // const pos = evt.latlng;
        // const path = this.drivingPaths.filter(p => p.id === index)[0];
        // // console.log(RainMap.TAG, path);
        // const pointOnPath = findClosestPointOnPathLatLng(path.points, pos)
        // // console.log(RainMap.TAG, pointOnPath);
        // const alt = evt.latlng.alt;
        // evt.layer.setLatLng(new L.LatLng(pointOnPath.lat, pointOnPath.lng, alt));
        //
        // if (pointOnPath.globalT === 0) {
        //     // console.log(RainMap.TAG, path);
        //     path.startExtensionLine?.removeFrom(this.map);
        //     path.startExtensionLine = undefined;
        // } else if (pointOnPath.globalT === 1) {
        //     // console.log(RainMap.TAG, path);
        //     path.endExtensionLine?.removeFrom(this.map);
        //     path.endExtensionLine = undefined;
        // } else {
        //     if (!path.startExtensionLine) {
        //         path.startExtensionLine = L.polyline(path.startExtensionPoints, generatedLinePathExtensionOptions);
        //         path.startExtensionLine.addTo(this.map);
        //     }
        //     if (!path.endExtensionLine) {
        //         path.endExtensionLine = L.polyline(path.endExtensionPoints, generatedLinePathExtensionOptions);
        //         path.endExtensionLine.addTo(this.map);
        //     }
        // }

    }


    clearLinesAndMarkers() {
        this.drivingPaths.forEach(path  => {
            path.polyLine?.removeFrom(this.map);
            // path.startExtensionLine?.removeFrom(this.map);
            // path.endExtensionLine?.removeFrom(this.map);
        });
        this.drivingPaths = [];

        this.controlPoints.forEach(point => {
            point.removeFrom(this.map);
        });
        this.controlPoints = [];

        this.servicePathLines.forEach(path => {
            path?.removeFrom(this.map);
        });
        this.servicePathLines = [];

        this.nodeMarkers.forEach(marker => {
            marker.removeFrom(this.map);
        });
        this.nodeMarkers = [];

        this.clearRouteMarkers();
        this.clearRouteLengthMarkers();

        this.wateringRoutesPickedIndices = [];

        this.clearWateringRoutePoints();
        this.clearWateringRouteLines();
        this.clearWateringPolygons();
        this.clearWateringPathsLines();
    }

    clearRouteMarkers() {
        this.routeMarkers.forEach(marker => {
            marker.removeFrom(this.map);
        });
        this.routeMarkers = [];
    }

    clearRouteLengthMarkers() {
        this.routeLengthMarkers.forEach(marker => {
            marker.removeFrom(this.map);
        });
        this.routeLengthMarkers = [];
    }

    clearWateringPathsLines() {
        this.wateringMainPathsLines.forEach(line => {
            line.removeFrom(this.map);
        })
        this.wateringMainPathsLines = [];

        this.wateringSidePathsLines.forEach(line => {
            line.removeFrom(this.map);
        })
        this.wateringSidePathsLines = [];
    }

    clearWateringRoutePoints() {
        this.wateringRoutePoints.forEach(p => {
            p.removeFrom(this.map);
        })
        this.wateringRoutePoints = [];
    }

    clearWateringRouteLines() {
        this.wateringRouteLines.forEach(l => {
            l.removeFrom(this.map);
        })
        this.wateringRouteLines = [];
    }

    clearWateringPolygons() {
        this.wateringPolygons.forEach(polygon => {
            polygon.removeFrom(this.map);
        })
        this.wateringPolygons = [];
    }


    beginRoutePlanning(corridorWidth, curveRadius) {
        if (!this.graph) return;
        console.log(RainMap.TAG, "reset", this.graph);

        console.log(RainMap.TAG, "corridor", corridorWidth, "radius", curveRadius);
        this.corridorWidth = corridorWidth;
        this.curveRadius = curveRadius;

        this.clearLinesAndMarkers();
        this.waterSourceMarker?.removeFrom(this.map);

        let graphLatLng = this.convertGraphToLatLng(this.graph);

        this.clearWateringRouteLines();
        const closestNode = M.findClosestNodeIdXY(this.graph, this.waterSourcePos);
        this.wateringMainPathsIndices = [[closestNode], [closestNode]];
        this.wateringRoutesPickedIndices = [[closestNode], [closestNode]];
        this.wateringSequence = [];

        this.drawGraphLatLng(graphLatLng);

        this.nodeMarkers.forEach(marker => {
            marker.removeFrom(this.map);
        });
        this.nodeMarkers = [];

        for (let i = 0; i < graphLatLng.nodesLatLng.length; ++i) {
            let coord = graphLatLng.nodesLatLng[i];
            let marker = L.circleMarker(coord, {...C.wateringPointOptions, id: i});
            marker.addTo(this.map);
            this.nodeMarkers.push(marker);

            marker.on('mousedown', (e) => {
                let layer = e.target;
                let id = layer.options.id;
                console.log(RainMap.TAG, "clicked node id", id);
                console.log(RainMap.TAG, "route picked indices:", this.wateringRoutesPickedIndices[0], this.wateringRoutesPickedIndices[1]);
                console.log(RainMap.TAG, "route main indices:", this.wateringMainPathsIndices[0], this.wateringMainPathsIndices[1]);

                if (this.wateringRoutesPickedIndices[0].at(-1) === id) {
                    if (this.wateringRoutesPickedIndices[0].length > 1) {
                        this.shortenRouteIndices(0, id);
                    }
                } else if (this.wateringRoutesPickedIndices[1].at(-1) === id) {
                    if (this.wateringRoutesPickedIndices[1].length > 1) {
                        this.shortenRouteIndices(1, id);
                    }
                } else {
                    if (!this.wateringMainPathsIndices.some(path => path.includes(id))) {
                        const lastIndices = [this.wateringMainPathsIndices[0].at(-1), this.wateringMainPathsIndices[1].at(-1)];
                        const routes = [M.findShortestPathWeighted(graphLatLng, lastIndices[0], id), M.findShortestPathWeighted(graphLatLng, lastIndices[1], id)];
                        console.log(RainMap.TAG, "routes", routes);
                        if (routes[0].length <= routes[1].length && !routes[0].slice(1).some(r => this.wateringMainPathsIndices[0].includes(r))) {
                            this.wateringRoutesPickedIndices[0].push(id);
                            this.wateringMainPathsIndices[0].push(...routes[0].slice(1));
                        } else if (!routes[1].slice(1).some(r => this.wateringMainPathsIndices[1].includes(r))) {
                            this.wateringRoutesPickedIndices[1].push(id);
                            this.wateringMainPathsIndices[1].push(...routes[1].slice(1));
                        }
                    }
                }

                console.log(RainMap.TAG, this.wateringMainPathsIndices);

                this.updateWatering();
            });
        }
    }


    shortenRouteIndices(index, nodeId) {
        if (this.wateringRoutesPickedIndices[index].at(-1) === nodeId) {
            this.wateringRoutesPickedIndices[index].pop();
            while (this.wateringMainPathsIndices[index].length > 1 && this.wateringMainPathsIndices[index].at(-1) !== this.wateringRoutesPickedIndices[index].at(-1)) {
                this.wateringMainPathsIndices[index].pop();
            }
        }
    }


    updateWatering() {

        this.wateringSidePathsIndices = this.generateSidePathsIndices(this.graphLatLng, this.curveRadius);
        console.log(RainMap.TAG, "sidepaths indices", this.wateringSidePathsIndices);

        console.log(RainMap.TAG, "WATER", "main paths indices", this.wateringMainPathsIndices);
        console.log(RainMap.TAG, "WATER", "side paths indices", this.wateringSidePathsIndices);

        this.updateWateringVisualization(this.graphLatLng, this.wateringMainPathsIndices, this.wateringSidePathsIndices);

    }


    updateWateringVisualization(graphLatLng, wateringMainPathsIndices, wateringSidePathsIndices) {

        this.clearRouteLengthMarkers();
        this.clearWateringPathsLines();
        this.clearWateringRoutePoints();
        this.clearWateringRouteLines();
        this.clearWateringPolygons();
        this.clearRouteMarkers();

        const radius = this.curveRadius * RainMap.SCALE;

        this.mainPathsWorkZones = [];
        this.sidePathsWorkZones = [];

        let usedSidePaths = [];

        wateringMainPathsIndices.forEach(mainPathIndices => {

            let routeEndMarker = L.circleMarker(graphLatLng.nodesLatLng[mainPathIndices.at(-1)], C.activeWateringPointOptions);
            routeEndMarker.addTo(this.map);
            this.wateringRoutePoints.push(routeEndMarker);

            if (mainPathIndices.length > 1) {
                const {mainPathLine, sidePathsLines, wateredSidePathsLines}
                    = this.calculateRoundedPaths(graphLatLng, mainPathIndices, wateringSidePathsIndices, radius, usedSidePaths);

                this.updateWateringPolygons(mainPathLine, wateredSidePathsLines, this.corridorWidth);

                sidePathsLines.forEach(path => {
                    const pathLatLng = this.getPathLatLng(path);
                    const line = L.polyline(pathLatLng, C.activeWateringSequenceOptions);
                    line.addTo(this.map);
                    this.wateringSidePathsLines.push(line);

                    let pathLength = this.calculatePathLength(path, mainPathLine.map(l => l.point));
                    let marker = L.circleMarker(this.getPointLatLng(path.at(-1)), {...C.wateringPointOptions, interactive: false});
                    marker.addTo(this.map);
                    marker.bindTooltip("" + Math.round(pathLength / RainMap.SCALE), {permanent: true, direction: 'top'}).openTooltip();
                    this.routeLengthMarkers.push(marker);

                });

                console.log(RainMap.TAG, "route line", mainPathLine);
                const routeLineLatLng = this.getPathLatLng(mainPathLine.map(rl => rl.point));
                const line = L.polyline(routeLineLatLng, C.activeWateringPathOptions);
                line.addTo(this.map);
                this.wateringRouteLines.push(line);

                const pathLength = M.getLinearDistanceLengthXY(mainPathLine.map(l => l.point));
                let lengthMarker = L.circleMarker(graphLatLng.nodesLatLng[mainPathIndices.at(-1)], {...C.wateringPointOptions, interactive: false});
                lengthMarker.addTo(this.map);
                lengthMarker.bindTooltip("" + Math.round(pathLength / RainMap.SCALE), {permanent: true, direction: 'bottom'}).openTooltip();
                this.routeLengthMarkers.push(lengthMarker);
            }
        });
    }


    calculatePathLength(sidePath, mainPath) {
        const touchPoint = M.calculateClosestPointOnPathXY(mainPath, sidePath[0]);
        const truncatedMainPath = M.truncatePathEndingAtPointXY(mainPath, mainPath[0], touchPoint);

        const mainLength = M.getLinearDistanceLengthXY(truncatedMainPath);
        const sideLength = M.getLinearDistanceLengthXY(sidePath);
        const betweenDist = M.distXY(truncatedMainPath.at(-1), sidePath.at(0));

        return mainLength + betweenDist + sideLength;
    }


    calculateRoundedPaths(graph, mainPathIndices, sidePathsIndices, radius, usedSidePathsRef) {
        let mainPath = [{point: graph.nodes[mainPathIndices.at(0)], water: false}];
        let sidePaths = [];
        let wateredSidePaths = [];

        for (let i = 1; i < mainPathIndices.length; ++i) {

            const isLastIdx = i === mainPathIndices.length - 1;

            const intersection = graph.nodes[mainPathIndices[i]];
            const prevPoints = M.getTwoPathPointsFromSrcNodeXY(graph, mainPathIndices[i], mainPathIndices[i - 1], radius / 10);
            const prevVector = M.subXY(prevPoints[1], prevPoints[0]);
            const nextPoints = isLastIdx ? [] : M.getTwoPathPointsFromSrcNodeXY(graph, mainPathIndices[i], mainPathIndices[i + 1], radius / 10)
            const nextVector = isLastIdx ? M.subXY(prevPoints[0], prevPoints[1]) : M.subXY(nextPoints[1], nextPoints[0]);

            const pointOnPrev = isLastIdx
                ? {...graph.nodes[mainPathIndices[i]], isStraight: true}
                : M.getContactPointForCircleXY(intersection, prevVector, nextVector, radius);
            const pointOnNext = isLastIdx ? pointOnPrev : M.getContactPointForCircleXY(intersection, nextVector, prevVector, radius);
            const pointsOnCircle = isLastIdx ? [] : M.getPointsOnCircleXY(intersection, prevVector, nextVector, pointOnPrev, pointOnNext, radius);

            const prevPath = M.getPath(graph, mainPathIndices[i - 1], mainPathIndices[i]);
            if (prevPath.length > 2) {
                const prevMidpoint = M.getMidpointOfPathXY(prevPath);
                const halfPath = M.truncatePathXY(prevPath, prevPath[0], prevMidpoint, pointOnPrev);
                halfPath.forEach(p => {
                    mainPath.push({point: p, water: true});
                });
            }

            mainPath.push({point: pointOnPrev, water: false});
            pointsOnCircle.forEach(p => {
                mainPath.push({point: p, water: false});
            });
            mainPath.push({point: pointOnNext, water: true});

            if (!isLastIdx) {
                const nextPath = M.getPath(graph, mainPathIndices[i], mainPathIndices[i + 1]);
                if (nextPath.length > 2) {
                    const nextMidpoint = M.getMidpointOfPathXY(nextPath);
                    const halfPath = M.truncatePathXY(nextPath, nextPath[0], pointOnNext, nextMidpoint);
                    halfPath.forEach(p => {
                        mainPath.push({point: p, water: true});
                    });
                }
            }


            for (let k = 0; k < sidePathsIndices.length; ++k) {
                if (!usedSidePathsRef.includes(k)) {
                    const sidePath = sidePathsIndices[k];
                    // console.log(RainMap.TAG, "main path indices", mainPathIndices, "side path", sidePath);
                    // console.log(RainMap.TAG, "main path i - 1", mainPathIndices[i - 1], "main path i", mainPathIndices[i], "side path", sidePath);
                    if (sidePath[0] === mainPathIndices[i - 1] && sidePath[1] === mainPathIndices[i]) {
                        let {fullLine, wateredLine} = this.calculateRoundedSidePath(graph, sidePath, radius);
                        console.log(RainMap.TAG, "adding side path", sidePath, "full line", fullLine, "watered line", wateredLine);
                        if (!pointOnPrev.isStraight) {
                            if (fullLine[0].isStraight) {
                                wateredLine.unshift(pointOnPrev);
                            }
                            fullLine.unshift(pointOnPrev);
                        }
                        sidePaths.push(fullLine);
                        wateredSidePaths.push(wateredLine);
                        usedSidePathsRef.push(k);
                    }
                }
            }
        }

        return {mainPathLine: mainPath, sidePathsLines: sidePaths, wateredSidePathsLines: wateredSidePaths};
    }



    calculateRoundedSidePath(graph, sidePathIndices, radius) {
        const intersection = graph.nodes[sidePathIndices[1]];
        const pathPointsToPrevious = M.getTwoPathPointsFromSrcNodeXY(graph, sidePathIndices[1], sidePathIndices[0], radius);
        let vectorToPrevious = M.subXY(pathPointsToPrevious[1], pathPointsToPrevious[0]);
        console.log(RainMap.TAG, "path to previous points", pathPointsToPrevious);

        const pathPointsToChild = M.getTwoPathPointsFromSrcNodeXY(graph, sidePathIndices[1], sidePathIndices[2], radius);
        let vectorToChild = M.subXY(pathPointsToChild[1], pathPointsToChild[0]);
        let contactPointOnPrev = M.getContactPointForCircleXY(intersection, vectorToPrevious, vectorToChild, radius);
        let contactPointOnChild = M.getContactPointForCircleXY(intersection, vectorToChild, vectorToPrevious, radius);

        const prevEdge = M.findEdgeIndex(graph, sidePathIndices[0], sidePathIndices[1]);
        const pointOnPrevious = M.calculateClosestPointOnPathXY(graph.paths[prevEdge], contactPointOnPrev);
        const childEdge = M.findEdgeIndex(graph, sidePathIndices[1], sidePathIndices[2]);
        const pointOnChildPath = M.calculateClosestPointOnPathXY(graph.paths[childEdge], contactPointOnChild);

        vectorToPrevious = M.subXY(pointOnPrevious, pathPointsToPrevious[0]);
        vectorToChild = M.subXY(pointOnChildPath, pathPointsToChild[0]);
        contactPointOnPrev = M.getContactPointForCircleXY(intersection, vectorToPrevious, vectorToChild, radius);
        contactPointOnChild = M.getContactPointForCircleXY(intersection, vectorToChild, vectorToPrevious, radius);

        const pointsOnCircle = M.getPointsOnCircleXY(intersection, vectorToPrevious, vectorToChild, contactPointOnPrev, contactPointOnChild, radius);

        let fullPathLine = [contactPointOnPrev, ...pointsOnCircle, contactPointOnChild];
        let wateredPathLine = contactPointOnPrev.isStraight ? [contactPointOnPrev, contactPointOnChild] : [contactPointOnChild];

        if (sidePathIndices.length >= 3) {
            const edgeIdx = M.findEdgeIndex(graph, sidePathIndices[1], sidePathIndices[2]);
            const truncatedPath = M.truncatePathStartingAtPointXY(graph.paths[edgeIdx], graph.nodes[sidePathIndices[1]], contactPointOnChild);
            console.log(RainMap.TAG, "truncated path", truncatedPath);
            fullPathLine.push(...truncatedPath);
            wateredPathLine.push(...truncatedPath);
        }
        console.log(RainMap.TAG, "full path line", fullPathLine);
        for (let i = 2; i < sidePathIndices.length - 1; ++i) {
            const edgeIdx = M.findEdgeIndex(graph, sidePathIndices[i], sidePathIndices[i + 1]);
            const path = graph.paths[edgeIdx];
            console.log(RainMap.TAG, "path", path);
            const lastPoint = fullPathLine.at(-1);
            if (M.distXY(lastPoint, path.at(0)) < M.distXY(lastPoint, path.at(-1))) {
                fullPathLine.push(...path.slice(1));
                wateredPathLine.push(...path.slice(1));
            } else {
                fullPathLine.push(...path.toReversed().slice(1));
                wateredPathLine.push(...path.toReversed().slice(1));
            }
        }
        console.log(RainMap.TAG, "curved path", fullPathLine);

        // if (fullPathLine.every(p => isFinite(p.X) && isFinite(p.Y))) {
        //     let line = L.polyline(this.getPathLatLng(fullPathLine), C.activeWateringSequenceOptions);
        //     this.wateringSidePathsLines.push(line);
        //     line.addTo(this.map);
        // }
        // sidePathsCoords.push(wateredPathLine);

        return {fullLine: fullPathLine, wateredLine: wateredPathLine};
    }



    refreshSequence(corridorWidth, curveRadius) {
        console.log(RainMap.TAG, "refresh", this.graph);
        this.corridorWidth = corridorWidth;
        this.curveRadius = curveRadius;

        this.updateWateringVisualization(this.graphLatLng, this.wateringMainPathsIndices);
        // this.traverseWateringSequence(this.graphLatLng, this.wateringSequence, corridorWidth, curveRadius);
    }


    // traverseWateringSequence(graphLatLng, sequence, corridorWidth, curveRadius) {
    //
    //     const radius = curveRadius * RainMap.SCALE;
    //
    //     function traverse(treeNode, previousIdx, pathLength, depth) {
    //
    //         polylineIdx.push(treeNode.id);
    //         if (previousIdx < 0) {
    //             polyline.push({point: graphLatLng.nodes[treeNode.id], water: false});
    //         }
    //
    //         if (treeNode.children.length === 0) { // || depth > 3) {
    //             polyline.push({point: graphLatLng.nodes[treeNode.id], water: true});
    //             lengthMarkerData.push({point: graphLatLng.nodesLatLng[treeNode.id], pathLength: pathLength});
    //
    //         } else {
    //             if (previousIdx < 0) {
    //                 for (const child of treeNode.children) {
    //                     const edgeIndex = M.findEdgeIndex(graphLatLng, treeNode.id, child.id);
    //                     traverse(child, treeNode.id, pathLength + graphLatLng.pathLengths[edgeIndex], depth + 1);
    //                 }
    //             } else {
    //                 for (const child of treeNode.children) {
    //                     const intersection = graphLatLng.nodes[treeNode.id];
    //                     const pathToPrevious = M.getTwoPathPointsFromSrcNodeXY(graphLatLng, treeNode.id, previousIdx);
    //                     const vectorToPrevious = M.subXY(pathToPrevious[1], pathToPrevious[0]);
    //                     const pathToChild = M.getTwoPathPointsFromSrcNodeXY(graphLatLng, treeNode.id, child.id);
    //                     const vectorToChild = M.subXY(pathToChild[1], pathToChild[0]);
    //                     const pointOnPrev = M.getContactPointForCircleXY(intersection, vectorToPrevious, vectorToChild, radius);
    //                     const pointOnNext = M.getContactPointForCircleXY(intersection, vectorToChild, vectorToPrevious, radius);
    //                     const pointsOnCircle = M.getPointsOnCircleXY(intersection, vectorToPrevious, vectorToChild, pointOnPrev, pointOnNext, radius);
    //
    //                     polyline.push({point: pointOnPrev, water: pointOnPrev.isStraight});
    //                     polyline.push(...pointsOnCircle.map(p => ({point: p, water: false})));
    //                     polyline.push({point: pointOnNext, water: true});
    //
    //                     const edgeIndex = M.findEdgeIndex(graphLatLng, treeNode.id, child.id);
    //
    //                     let betweenPoints = [];
    //                     if (graphLatLng.paths[edgeIndex].length > 2) {
    //                         const pathLength = graphLatLng.pathLengths[edgeIndex];
    //                         const t = (2 * radius) / pathLength;
    //                         console.log(RainMap.TAG, "path len", pathLength, "t", t);
    //                         // betweenPoints.push(M.getPointAlongOfPathXY(graphLatLng.paths[edgeIndex], t));
    //                         betweenPoints.push(M.getPointAlongOfPathXY(graphLatLng.paths[edgeIndex], 0.5));
    //                         // betweenPoints.push(M.getPointAlongOfPathXY(graphLatLng.paths[edgeIndex], 1.0 - t));
    //                     }
    //
    //                     for (let p of betweenPoints) {
    //                         polyline.push({point: p, water: true});
    //                     }
    //
    //                     traverse(child, treeNode.id, pathLength + graphLatLng.pathLengths[edgeIndex], depth + 1);
    //
    //                     // polyline.push({point: midpoint, water: true});
    //                     for (let p of betweenPoints.toReversed()) {
    //                         polyline.push({point: p, water: true});
    //                     }
    //                     polyline.push({point: pointOnNext, water: pointOnNext.isStraight});
    //                     polyline.push(...pointsOnCircle.toReversed().map(p => ({point: p, water: false})));
    //                     polyline.push({point: pointOnPrev, water: depth > 1});
    //                 }
    //             }
    //         }
    //         if (previousIdx < 0) {
    //             polyline.push({point: graphLatLng.nodes[treeNode.id], water: false});
    //         }
    //     }
    //
    //     this.wateringRouteLine?.removeFrom(this.map);
    //     this.wateringSequenceLine?.removeFrom(this.map);
    //     this.routeMarkers.forEach(marker => {
    //         marker.removeFrom(this.map);
    //     });
    //     this.routeMarkers = [];
    //     this.clearRouteLengthMarkers();
    //
    //     if (sequence.length < 2) {
    //         this.clearWateringPolygons();
    //         return;
    //     }
    //
    //     const tree = this.convertRouteToTree(sequence);
    //
    //     let polylineIdx = [];
    //     let polyline = [];
    //
    //     // const distinctIndices = new Set(sequence);
    //     // distinctIndices.forEach(idx => {
    //     //     let marker = L.circleMarker(graphLatLng.nodesLatLng[idx], {...wateringPointOptions, interactive: false});
    //     //     marker.addTo(this.map);
    //     //     marker.bindTooltip("[" + idx + "]", {permanent: true, direction: 'right'}).openTooltip();
    //     //     this.routeMarkers.push(marker);
    //     // });
    //
    //     // console.log(RainMap.TAG, "tree", tree);
    //
    //     let lengthMarkerData = [];
    //
    //     traverse(tree,-1, 0, 0);
    //
    //     console.log(RainMap.TAG, "water polyline indices", polylineIdx);
    //     console.log(RainMap.TAG, "water polyline", polyline);
    //
    //     let points = polyline.map(l => l.point);
    //     // console.log(RainMap.TAG, "water points", points);
    //     const polylineLatLng = M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(points, this.averageLocalZ, RainMap.SCALE));
    //     // console.log(RainMap.TAG, "water polyline latlng", polylineLatLng);
    //     this.wateringRouteLine = L.polyline(polylineLatLng, C.activeWateringPathOptions);
    //
    //     lengthMarkerData.forEach(data => {
    //         let lengthMarker = L.circleMarker(data.point, {...C.wateringPointOptions, interactive: false});
    //         lengthMarker.addTo(this.map);
    //         lengthMarker.bindTooltip("" + Math.round(data.pathLength / RainMap.SCALE), {permanent: true, direction: 'top'}).openTooltip();
    //         this.routeLengthMarkers.push(lengthMarker);
    //     });
    //
    //     this.clearWateringPolygons();
    //     this.updateWaterRectangles(graphLatLng, polyline, corridorWidth);
    //     this.updateSidePathsWaterRectangles(graphLatLng, this.wateringSidePathsIndices, corridorWidth);
    //
    //     // for (let i = 0; i < polylineLatLng.length; ++i) {
    //     //     let marker = L.circleMarker(polylineLatLng[i], {...wateringPointOptions, interactive: false});
    //     //     marker.addTo(this.map);
    //     //     marker.bindTooltip("" + i, {permanent: true, direction: 'top'}).openTooltip();
    //     //     this.routeMarkers.push(marker);
    //     // }
    //
    //     this.wateringRouteLine.addTo(this.map);
    //
    // }





    // updateWateringRouteLine(graphLatLng) {
    //
    //     this.clearWateringRoutePoints();
    //     this.clearWateringRouteLines();
    //     this.routeLengthMarkers.forEach(marker => {
    //         marker.removeFrom(this.map);
    //     });
    //     this.routeLengthMarkers = [];
    //
    //     for (let i = 0; i <= 1; ++i) {
    //         if (this.wateringMainPathsIndices[i].length > 1) {
    //             const routeLine0 = this.calculateRoundedPaths(graphLatLng, this.wateringMainPathsIndices[i], this.wateringSidePathsIndices, this.wateringSidePathsCoords,this.curveRadius * RainMap.SCALE);
    //             console.log(RainMap.TAG, "route line", routeLine0);
    //             const routeLineLatLng = this.getPathLatLng(routeLine0.map(rl => rl.point));
    //             const line = L.polyline(routeLineLatLng, C.activeWateringPathOptions);
    //             line.addTo(this.map);
    //             this.wateringRouteLines.push(line);
    //         }
    //     }
    //
    //     return;
    //
    //     // const route = this.wateringMainPathsIndices[1].toReversed().concat(this.wateringMainPathsIndices[0].slice(1));
    //     // console.log(RainMap.TAG, "concat route", route);
    //     //
    //     // if (route.length >= 2) {
    //     //     const pathLinePoints = this.combinePathLines(graphLatLng, route);
    //     //     console.log(RainMap.TAG, "water line", pathLinePoints);
    //     //     if (route.length > 1) {
    //     //         this.wateringRouteLine = L.polyline(pathLinePoints, C.activeWateringPathOptions);
    //     //         this.wateringRouteLine.addTo(this.map);
    //     //     }
    //     //
    //     //     // for (let i = 0; i < this.wateringRouteIndices.length; ++i) {
    //     //     //     if (this.wateringRoutesPickedIndices.findIndex(value => value === this.wateringRouteIndices[i]) >= 0) {
    //     //     //         const pathHome = this.findShortestWayHomeInSequence(this.wateringRouteIndices.slice(0, i + 2), i);
    //     //     //         console.log(RainMap.TAG, "ROUTE", "path home", pathHome);
    //     //     //         let pathLength = M.getPathLengthXY(graphLatLng, pathHome.map(idx => this.wateringRouteIndices[idx]));
    //     //     //         let lengthMarker = L.circleMarker(graphLatLng.nodesLatLng[this.wateringRouteIndices[i]], {...C.wateringPointOptions, interactive: false});
    //     //     //         lengthMarker.addTo(this.map);
    //     //     //         lengthMarker.bindTooltip("" + Math.round(pathLength / RainMap.SCALE), {permanent: true, direction: 'top'}).openTooltip();
    //     //     //         this.routeLengthMarkers.push(lengthMarker);
    //     //     //     }
    //     //     // }
    //     // }
    //     //
    //     // if (route.length > 0) {
    //     //     let marker = L.circleMarker(graphLatLng.nodesLatLng[route.at(0)], C.activeWateringPointOptions);
    //     //     marker.addTo(this.map);
    //     //     this.wateringRoutePoints.push(marker);
    //     //     marker = L.circleMarker(graphLatLng.nodesLatLng[route.at(-1)], C.activeWateringPointOptions);
    //     //     marker.addTo(this.map);
    //     //     this.wateringRoutePoints.push(marker);
    //     // }
    // }


    // combinePathLines(graphLatLng, nodeIndices) {
    //
    //     function distLatLng(a, b) { return Math.hypot(a.lat - b.lat, a.lng - b.lng); }
    //
    //     let result = [];
    //
    //     for (let i = 0; i < nodeIndices.length - 1; ++i) {
    //         const s0 = nodeIndices[i];
    //         const s1 = nodeIndices[i + 1];
    //         const edgeIdx = graphLatLng.edges.findIndex(e => (e[0] === s0 && e[1] === s1) || (e[0] === s1 && e[1] === s0));
    //         if (edgeIdx >= 0) {
    //             const pathLatLng = graphLatLng.pathsLatLng[edgeIdx];
    //             const startNodeLatLng = graphLatLng.nodesLatLng[s0];
    //             const endNodeLatLng = graphLatLng.nodesLatLng[s1];
    //             if (distLatLng(pathLatLng[0], startNodeLatLng) < distLatLng(pathLatLng[0], endNodeLatLng)) {
    //                 result.push(...pathLatLng);
    //             } else {
    //                 result.push(...(pathLatLng.toReversed()));
    //             }
    //         }
    //     }
    //
    //     return result;
    // }




    generateSidePathsIndices(graph, curveRadius) {
        const radius = curveRadius * RainMap.SCALE;
        // const corridor = corridorWidth * RainMap.SCALE;
        // const geoFence = this.geoFence;

        function getEdges(nodeIdx) {
            return graph.edges.filter(e => e[0] === nodeIdx || e[1] === nodeIdx);
        }

        function filterEdgesByType(edges, type) {
            return edges.filter(e => {
                const edgeIndex = M.findEdgeIndex(graph, e[0], e[1]);
                return (edgeIndex >= 0 && graph.edgeTypes[edgeIndex] === type);
            })
        }

        function getWateringPathRecursive(partialSidePath, nodeIdx, parentNodeIdx, allowExtensions) {

            partialSidePath.push(nodeIdx);

            const candidateEdges = getEdges(nodeIdx).filter(edge => {
                let endIdx = nodeIdx === edge[0] ? edge[1] : edge[0];
                return endIdx !== parentNodeIdx;
            });
            console.log(RainMap.TAG, "1) parent", parentNodeIdx, "node", nodeIdx, "candidates", JSON.stringify(candidateEdges));

            let wateringEdges = filterEdgesByType(candidateEdges, C.pathTypeWatering)
            console.log(RainMap.TAG, "2) parent", parentNodeIdx, "node", nodeIdx, "candidates", JSON.stringify(wateringEdges));

            if (wateringEdges.length === 0 && allowExtensions) {
                wateringEdges = filterEdgesByType(candidateEdges, C.pathTypeWateringExtension);
            }
            console.log(RainMap.TAG, "3) parent", parentNodeIdx, "node", nodeIdx, "candidates", JSON.stringify(wateringEdges));

            let dotProductLimit = -0.6;
            if (parentNodeIdx >= 0) {
                const edgeIdx = M.findEdgeIndex(graph, nodeIdx, parentNodeIdx);
                if (graph.pathLengths[edgeIdx] < 1.9 * radius) {
                    dotProductLimit = 0.5;
                }
            }
            const mainPathDirection = M.normalizeXY(M.subXY(graph.nodes[nodeIdx], graph.nodes[parentNodeIdx]));

            wateringEdges = wateringEdges.filter(edge => {
                let endIdx = nodeIdx === edge[0] ? edge[1] : edge[0];
                const edgeDirection = M.normalizeXY(M.subXY(graph.nodes[endIdx], graph.nodes[nodeIdx]));
                const dotProduct = M.dotXY(mainPathDirection, edgeDirection);
                return (dotProduct >= dotProductLimit && !partialSidePath.includes(endIdx) && !sidePaths.flat().includes(endIdx));
            });

            if (wateringEdges.length > 0) {
                wateringEdges.sort(edge => graph.pathLengths[M.findEdgeIndex(graph, edge[0], edge[1])]);
                let edge = wateringEdges.at(-1);
                let endIdx = nodeIdx === edge[0] ? edge[1] : edge[0];
                console.log(RainMap.TAG, "node idx", nodeIdx);
                getWateringPathRecursive(partialSidePath, endIdx, nodeIdx, true);
            }
        }

        function getWateringPaths(nodeIdx, parentNodeIdx, nextRouteNodeIdx) {
            const candidateEdges = getEdges(nodeIdx).filter(edge => {
                let endIdx = nodeIdx === edge[0] ? edge[1] : edge[0];
                return endIdx !== parentNodeIdx;
            });
            let wateringEdges = filterEdgesByType(candidateEdges, C.pathTypeWatering);
            if (wateringEdges.length === 0) {
                wateringEdges = filterEdgesByType(candidateEdges, C.pathTypeWateringExtension);
            }

            let dotProductLimit = -0.966;
            if (parentNodeIdx >= 0) {
                const edgeIdx = M.findEdgeIndex(graph, nodeIdx, parentNodeIdx);
                if (graph.pathLengths[edgeIdx] < 1.9 * radius) {
                    dotProductLimit = 0.259;
                }
            }

            const mainPathDirection = M.normalizeXY(M.subXY(graph.nodes[nodeIdx], graph.nodes[parentNodeIdx]));
            wateringEdges.forEach(edge => {
                let endIdx = nodeIdx === edge[0] ? edge[1] : edge[0];
                const edgeDirection = M.normalizeXY(M.subXY(graph.nodes[endIdx], graph.nodes[nodeIdx]));
                const dotProduct = M.dotXY(mainPathDirection, edgeDirection);
                if (dotProduct > dotProductLimit) {
                    if (!sidePaths.some(path => path.includes(endIdx)) && endIdx !== nextRouteNodeIdx) {
                        let sidePath = [parentNodeIdx, nodeIdx];
                        getWateringPathRecursive(sidePath, endIdx, nodeIdx, true);
                        if (sidePath.length > 2) {
                            if (!sidePaths.some(sp => M.areArraysEqual(sp, sidePath))) {
                                sidePaths.push(sidePath);
                            }
                        }
                    }
                }
            })
        }

        let sidePaths = [];

        this.wateringMainPathsIndices.forEach(mainPath => {
            for (let i = 1; i < mainPath.length; ++i) {
                let nextIdx = i === mainPath.length - 1 ? i : i + 1;
                getWateringPaths(mainPath[i], mainPath[i - 1], mainPath[nextIdx]);
            }
        })

        return sidePaths;
    }


    // findShortestWayHomeInSequence(sequence, currentIndex) {
    //     // console.log(RainMap.TAG, "find home from sequence", JSON.stringify(sequence), "current index", currentIndex);
    //     let path = [currentIndex];
    //     while (path[0] > 0) {
    //         // console.log(RainMap.TAG, "calculating path", "indices:", JSON.stringify(path), "node ids:", JSON.stringify(path.map(p => sequence[p])));
    //         const firstOccurrenceIdx = sequence.findIndex(value => value === sequence[path[0]]);
    //         if (firstOccurrenceIdx < 0) {
    //             path.unshift(path[0] - 1);
    //         } else if (firstOccurrenceIdx === 0) {
    //             path.unshift(0);
    //         } else {
    //             path.unshift(firstOccurrenceIdx - 1);
    //         }
    //     }
    //
    //     // console.log(RainMap.TAG, "find home result", "indices:", JSON.stringify(path), "node ids:", JSON.stringify(path.map(p => sequence[p])));
    //
    //     let pathIdx = 0;
    //     while (pathIdx < path.length - 1) {
    //         if (sequence[path[pathIdx]] === sequence[path[pathIdx + 1]]) {
    //             path.splice(pathIdx + 1, 1);
    //         } else {
    //             ++pathIdx;
    //         }
    //     }
    //
    //     // console.log(RainMap.TAG, "find home result after shortening", "indices:", JSON.stringify(path), "node ids:", JSON.stringify(path.map(p => sequence[p])));
    //     return path;
    // }


    // updateWateringSidePathLines(graphLatLng, mainPathsIndices, sidePathsIndices, curveRadius) {
    //
    //     const radius = curveRadius * RainMap.SCALE;
    //     let sidePathsCoords = [];
    //
    //     sidePathsIndices.forEach(pathIndices => {
    //         const intersection = graphLatLng.nodes[pathIndices[1]];
    //         const pathToPrevious = M.getTwoPathPointsFromSrcNodeXY(graphLatLng, pathIndices[1], pathIndices[0], radius / 2);
    //         console.log(RainMap.TAG, "path to previous points", pathToPrevious);
    //         const vectorToPrevious = M.subXY(pathToPrevious[1], pathToPrevious[0]);
    //         const pathToChild = M.getTwoPathPointsFromSrcNodeXY(graphLatLng, pathIndices[1], pathIndices[2], radius / 2);
    //         console.log(RainMap.TAG, "path to child points", pathToChild);
    //         const vectorToChild = M.subXY(pathToChild[1], pathToChild[0]);
    //         const pointOnPrev = M.getContactPointForCircleXY(intersection, vectorToPrevious, vectorToChild, radius);
    //         const pointOnNext = M.getContactPointForCircleXY(intersection, vectorToChild, vectorToPrevious, radius);
    //         const pointsOnCircle = M.getPointsOnCircleXY(intersection, vectorToPrevious, vectorToChild, pointOnPrev, pointOnNext, radius);
    //
    //         let fullPathLine = [pointOnPrev, ...pointsOnCircle, pointOnNext];
    //         let wateredPathLine = pointOnPrev.isStraight ? [pointOnPrev, pointOnNext] : [pointOnNext];
    //
    //         if (pathIndices.length >= 3) {
    //             const edgeIdx = findEdgeIndex(graphLatLng, pathIndices[1], pathIndices[2]);
    //             const truncatedPath = truncatePathStartingAtPointXY(graphLatLng.paths[edgeIdx], graphLatLng.nodes[pathIndices[1]], pointOnNext);
    //             console.log(RainMap.TAG, "truncated path", truncatedPath);
    //             fullPathLine.push(...truncatedPath);
    //             wateredPathLine.push(...truncatedPath);
    //         }
    //         console.log(RainMap.TAG, "full path line", fullPathLine);
    //         for (let i = 2; i < pathIndices.length - 1; ++i) {
    //             const edgeIdx = findEdgeIndex(graphLatLng, pathIndices[i], pathIndices[i + 1]);
    //             const path = graphLatLng.paths[edgeIdx];
    //             console.log(RainMap.TAG, "path", path);
    //             const lastPoint = fullPathLine.at(-1);
    //             if (distXY(lastPoint, path.at(0)) < distXY(lastPoint, path.at(-1))) {
    //                 fullPathLine.push(...path.slice(1));
    //                 wateredPathLine.push(...path.slice(1));
    //             } else {
    //                 fullPathLine.push(...path.toReversed().slice(1));
    //                 wateredPathLine.push(...path.toReversed().slice(1));
    //             }
    //         }
    //         console.log(RainMap.TAG, "curved path", fullPathLine);
    //
    //         if (fullPathLine.every(p => isFinite(p.X) && isFinite(p.Y))) {
    //             let line = L.polyline(this.getPathLatLng(fullPathLine), C.activeWateringSequenceOptions);
    //             this.wateringSidePathsLines.push(line);
    //             line.addTo(this.map);
    //         }
    //         sidePathsCoords.push(wateredPathLine);
    //
    //         let connectedPath = this.concatSidePathWithMainPath(mainPathsIndices, pathIndices);
    //         console.log(RainMap.TAG, "connected path", connectedPath);
    //
    //         let pathLength = M.getPathLengthXY(graphLatLng, connectedPath); // TODO measure proper curve length
    //         let marker = L.circleMarker(graphLatLng.nodesLatLng[connectedPath.at(-1)], {...C.wateringPointOptions, interactive: false});
    //         marker.addTo(this.map);
    //         marker.bindTooltip("" + Math.round(pathLength / RainMap.SCALE), {permanent: true, direction: 'top'}).openTooltip();
    //         this.routeLengthMarkers.push(marker);
    //     });
    //
    //     return sidePathsCoords;
    // }


    updateWateringPolygons(mainPathLine, sidePathsCoords, corridorWidth) {
        console.log(RainMap.TAG, "watering main path line", mainPathLine);

        const offset = 0.5 * corridorWidth * RainMap.SCALE;
        let line = [];
        let isWaterOn = false;
        for (let i = 0; i < mainPathLine.length; ++i) {
            if (isWaterOn) {
                line.push(mainPathLine[i].point);
                if (!mainPathLine[i].water || i === mainPathLine.length - 1) {
                    let poly = this.generateOffsetPolygon(line, offset);
                    const polyPoints = this.getPathLatLng(poly);
                    this.mainPathsWorkZones.push(polyPoints);
                    const polygon = L.polyline(polyPoints, C.waterApplicationPolygonOptions);
                    polygon.addTo(this.map);
                    this.wateringPolygons.push(polygon);
                    isWaterOn = false;
                }
            } else {
                if (mainPathLine[i].water) {
                    line = [mainPathLine[i].point];
                    isWaterOn = true;
                }
            }
        }

        let sidePolygons = this.generateSidePathControlPolygons(sidePathsCoords, corridorWidth);
        sidePolygons.forEach(poly => {
            // console.log(RainMap.TAG, "polyline", poly);
            const polyPoints = this.getPathLatLng(poly);
            // console.log(RainMap.TAG, "poly points", polyPoints);
            this.sidePathsWorkZones.push(polyPoints);
            const polygon = L.polyline(polyPoints, C.waterApplicationPolygonOptions);
            polygon.addTo(this.map);
            this.wateringPolygons.push(polygon);

            const coord = M.getLatLngBounds(polyPoints).getCenter();
            let marker = L.circleMarker(coord, {...C.wateringPointOptions, interactive: false});
            marker.addTo(this.map);
            const number = this.sidePathsWorkZones.length;
            marker.bindTooltip("#" + number, {permanent: true, direction: 'left'}).openTooltip();
            this.routeMarkers.push(marker);
        });

    }


    // concatSidePathWithMainPath(mainPaths, sidePath) {
    //     for (let i = 0; i < mainPaths.length; ++i) {
    //         const mp = mainPaths[i];
    //         let idx = -1;
    //         for (let k = 0; k < mp.length - 1; ++k) {
    //             if (mp[k] === sidePath[0] && mp[k + 1] === sidePath[1]) {
    //                 idx = k;
    //                 break;
    //             }
    //         }
    //         if (idx >= 0) {
    //             return mp.slice(0, idx).concat(sidePath);
    //         }
    //     }
    //     return undefined;
    // }


    // updateWateringSequenceLine(graphLatLng, sequence) {
    //     console.log(RainMap.TAG, "watering sequence", sequence);
    //     const points = sequence.map(index => graphLatLng.nodesLatLng[index]);
    //     console.log(RainMap.TAG, "watering sequence line points", points);
    //
    //     const pathLine = this.combinePathLines(graphLatLng, sequence);
    //
    //     this.wateringSequenceLine?.removeFrom(this.map);
    //     this.wateringSequenceLine = L.polyline(pathLine, C.activeWateringSequenceOptions);
    //     this.wateringSequenceLine.addTo(this.map);
    //
    //     this.clearRouteMarkers();
    //     let wateringSequenceNumbers = new Map();
    //
    //     console.log(RainMap.TAG, "sequence", sequence);
    //     for (let i = 1; i < sequence.length - 1; ++i) {
    //         if (sequence[i - 1] === sequence[i + 1]) {
    //             const path = this.findShortestWayHomeInSequence(sequence, i);
    //             // console.log(RainMap.TAG, "path from", sequence[i], ":", path.map(p => sequence[p]));
    //             let pathLength = 0;
    //             for (let i = 0; i < path.length - 1; ++i) {
    //                 const s0 = sequence[path[i]];
    //                 const s1 = sequence[path[i + 1]];
    //                 const edgeIdx = graphLatLng.edges.findIndex(e => (e[0] === s0 && e[1] === s1) || (e[0] === s1 && e[1] === s0));
    //                 // console.log(RainMap.TAG, "edge from", path[i], "to", path[i+1], edgeIdx);
    //                 if (edgeIdx >= 0) {
    //                     if (!wateringSequenceNumbers.has(edgeIdx)) {
    //                         if (graphLatLng.edgeTypes[edgeIdx] === C.pathTypeWatering) {
    //                             // let middle = M.getLatLngAverage([graphLatLng.nodesLatLng[s0], graphLatLng.nodesLatLng[s1]]);
    //                             // let marker = L.circleMarker(middle, {...C.wateringPointOptions, interactive: false});
    //                             // marker.addTo(this.map);
    //                             // const number = wateringSequenceNumbers.size + 1;
    //                             // wateringSequenceNumbers.set(edgeIdx, number);
    //                             // marker.bindTooltip("#" + number, {permanent: true, direction: 'left'}).openTooltip();
    //                             // this.routeMarkers.push(marker);
    //                         }
    //                     }
    //                     pathLength += graphLatLng.pathLengths[edgeIdx] / RainMap.SCALE;
    //                 } else {
    //                     console.error(RainMap.TAG, "edge from index", path[i], "to", path[i+1], edgeIdx, "node ids: ", s0, s1); // FIXME
    //                 }
    //             }
    //             console.log(RainMap.TAG, "path from", sequence[i], ":", path.map(p => sequence[p]), "length", pathLength);
    //
    //             let marker = L.circleMarker(points[i], {...C.wateringPointOptions, interactive: false});
    //             marker.addTo(this.map);
    //             marker.bindTooltip("" + Math.round(pathLength), {permanent: true, direction: 'bottom'}).openTooltip();
    //             this.routeMarkers.push(marker);
    //         }
    //     }
    //
    // }


    // clearRouteMarkers() {
    //     this.routeMarkers.forEach(marker => {
    //         marker.removeFrom(this.map);
    //     });
    //     this.routeMarkers = [];
    // }




    // updateWaterRectangles(graph, waterIndexSequence, corridorWidth) {
    //
    //     for (let i = 0; i < waterIndexSequence.length - 1; ++i) {
    //         if (waterIndexSequence[i].water) {
    //             const p = waterIndexSequence[i].point;
    //             const q = waterIndexSequence[i + 1].point;
    //             const rect = M.calculateRectangleXY(p, q, 0, 0, corridorWidth * RainMap.SCALE);
    //             if (rect && rect.length === 4) {
    //                 if (rect.every((p) => p.X && p.Y)) {
    //                     const rectPoints = M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(rect, this.averageLocalZ, RainMap.SCALE));
    //                     const poly = L.polygon(rectPoints, C.waterApplicationPolygonOptions);
    //                     poly.addTo(this.map);
    //                     this.wateringPolygons.push(poly);
    //                 } else {
    //                     console.log(RainMap.TAG, "from", p, "to", q, "i", i, "water rectangle", rect);
    //                 }
    //             }
    //         }
    //     }
    // }

    // updateSidePathsWaterRectangles(graphLatLng, paths, corridorWidth) {
    //     for (let i = 0; i < paths.length; ++i) {
    //         let path = paths[i];
    //         for (let k = 1; k < path.length - 1; ++k) {
    //             const p = graphLatLng.nodes[path[k]];
    //             const q = graphLatLng.nodes[path[k + 1]];
    //             const rect = M.calculateRectangleXY(p, q, 0, 0, corridorWidth * RainMap.SCALE);
    //             if (rect && rect.length === 4) {
    //                 if (rect.every((p) => p.X && p.Y)) {
    //                     const rectPoints = M.getGlobalCoordinates(this.originLatLng, M.convertFromWorkSpace(rect, this.averageLocalZ, RainMap.SCALE));
    //                     const poly = L.polygon(rectPoints, C.waterApplicationPolygonOptions);
    //                     poly.addTo(this.map);
    //                     this.wateringPolygons.push(poly);
    //                 } else {
    //                     console.log(RainMap.TAG, "from", p, "to", q, "i", i, "water rectangle", rect);
    //                 }
    //             }
    //         }
    //     }
    // }



    generateSidePathControlPolygons(sidePathsCoords, corridorWidthMeters) {
        const offset = 0.5 * corridorWidthMeters * RainMap.SCALE;
        let results = [];
        sidePathsCoords.forEach(pathPoints => {
            results.push(this.generateOffsetPolygon(pathPoints, offset));
        });
        return results;
    }


    generateOffsetPolygon(polyLinePoints, offset) {
        // console.log(RainMap.TAG, "generating polygon from", polyLinePoints);
        let clipperOffset = new ClipperLib.ClipperOffset();
        let polyTree = new ClipperLib.PolyTree();
        clipperOffset.AddPath(polyLinePoints, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etOpenButt);
        clipperOffset.Execute(polyTree, offset);
        let offsetPath = ClipperLib.Clipper.ClosedPathsFromPolyTree(polyTree);
        let closedPath = offsetPath[0];
        closedPath.push(closedPath[0]);
        // console.log(RainMap.TAG, "generating polygon result", closedPath);
        return closedPath;
    }
}



export default RainMap;